import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { useState } from "react";

import { ConfirmationDialog, ContextMenu, Icon, MenuItem } from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import { InterludeEventTypes, useEventPublish } from "../events";
import { NodeDesigner } from "../NodeDesigner";
import { NavigationThumbnail, type NavigationThumbnailProps } from "./NavigationThumbnail";
import { ThumbnailMenu } from "./Thumbnail";

const MenuItemContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;

  height: 100%;
`;

const addedTooltipProps = {
  open: true,
  disableFocusListener: true,
  disableHoverListener: true,
  disableTouchListener: true,
};

type Props = NavigationThumbnailProps & {
  menu: boolean;
  added: boolean;
  linked: boolean;
  onAddedComplete: () => void;
};

export const InterludeNavigationThumbnail = ({
  node,
  title,
  selected = false,
  menu,
  added,
  linked,
  onAddedComplete,
}: Props) => {
  const formatMessage = useFormatMessage(formatStrings);

  const publish = useEventPublish();

  const [open, setOpen] = useState(false);

  const handleDelete = () => publish(InterludeEventTypes.REMOVE_INTERLUDE_REQUESTED, { interludeId: node.id });

  const theme = useTheme();

  const slotProps = { tooltip: { sx: { top: "-10px" } } };

  const addedSlotProps = {
    arrow: { sx: { color: theme.surface } },
    tooltip: { sx: { backgroundColor: theme.surface, top: "-10px" } },
    popper: { sx: { pointerEvents: "auto" } },
  };

  const tooltipProps = added ? addedTooltipProps : {};

  const tooltipTitle = added ? (
    <div onBlur={onAddedComplete}>
      <NodeDesigner node={node} view="summary" />
    </div>
  ) : undefined;

  return (
    <NavigationThumbnail
      node={node}
      title={title}
      selected={selected}
      tooltipProps={{ title: tooltipTitle, ...tooltipProps, slotProps: added ? addedSlotProps : slotProps }}
      style={{ borderStyle: linked ? "solid" : "dashed" }}
    >
      {menu && (
        <ThumbnailMenu>
          <ContextMenu data-test="interlude-thumbnail-context-menu">
            <MenuItem key="remove" onClick={() => setOpen(true)}>
              <MenuItemContainer>
                <Icon icon="trash-alt" />
                <span>{formatMessage("delete")}</span>
              </MenuItemContainer>
            </MenuItem>
          </ContextMenu>
        </ThumbnailMenu>
      )}
      <ConfirmationDialog
        open={open}
        header={formatMessage("header", { title })}
        message={formatMessage("message")}
        confirmLabel={formatMessage("confirm")}
        cancelLabel={formatMessage("cancel")}
        onConfirm={(confirmed) => {
          setOpen(false);

          if (confirmed) {
            handleDelete();
          }
        }}
      />
    </NavigationThumbnail>
  );
};

const formatStrings = {
  fallbackTitle: {
    en: "Untitled",
  },
  delete: {
    en: "Delete",
  },
  header: {
    en: `Delete Overlay "{title}"?`,
  },
  message: {
    en: "Are you sure you want to delete this overlay? This cannot be undone.",
  },
  confirm: {
    en: "Yes, delete it",
  },
  cancel: {
    en: "No, keep it",
  },
} satisfies FormatMessages;
